import React, { FC, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@mui/material/Typography';

import {
  useApprove,
  useBalance,
} from '@services/contract/common/erc1155.contract';
import { useCreateOrder } from '@services/contract/fusion/marketplace.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import useEthPriceUsd from '@hooks/common/useEthPriceUsd';

import { modifyEvent } from '@helpers/common/form.helpers';

import {
  applyDecimals,
  formatBNWithCommas,
  toBN,
} from '@utils/formaters.utils';

import { amountYup, priceYup } from '@validators/vaults/common.validators';

import Button from '@components/common/Button';
import Input from '@components/common/Input';
import LoadingTypography from '@components/common/LoadingTypography';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import SwitchChainButton from '@components/common/SwitchChainButton';
import Tooltip from '@components/common/Tooltip';

import { DEFAULT_CHAIN_ID } from '@constants/common/chain.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import {
  ONLY_DIGITS_WITH_DECIMALS,
  ONLY_POSITIVE_INTEGERS,
} from '@constants/common/regex.constants';
import {
  FUSION_NFT_NAME,
  MARKETPLACE_TOKEN,
} from '@constants/common/tokens.constants';
import { Size } from '@constants/common/ui.constants';
import {
  MAX_MARKETPLACE_PRICE_PER_ONE,
  SELLER_MARKETPLACE_FEE,
} from '@constants/fusion/common.constants';
import { FUSION_NFT, MARKETPLACE } from '@constants/fusion/contracts.constants';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Amount]: string;
  [FieldNames.Price]: string;
};

const SellOrderModal: FC<any> = () => {
  const { classes } = useStyles();
  const { account } = useAuth();

  const {
    data: balance,
    isPending: isPendingBalance,
    refetch: refetchBalance,
  } = useBalance({
    nftAddress: FUSION_NFT,
    chainId: DEFAULT_CHAIN_ID,
  });

  const {
    isApproved,
    approve,
    isPendingApprove,
    refetch: refetchApprove,
  } = useApprove({
    spender: MARKETPLACE,
    nftAddress: FUSION_NFT,
    chainId: DEFAULT_CHAIN_ID,
  });

  const { writeContractAsync: createOrder, isPending: isPendingCreateOrder } =
    useCreateOrder();

  const validationSchema = yup.object().shape({
    amount: amountYup(toBN(balance)),
    price: priceYup(MAX_MARKETPLACE_PRICE_PER_ONE),
  });

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    formState: { isValid },
    watch,
    reset,
  } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(validationSchema),
    defaultValues: { [FieldNames.Amount]: '', [FieldNames.Price]: '' },
  });

  const inputAmount = watch(FieldNames.Amount);
  const inputPrice = watch(FieldNames.Price);

  const { data: ehtPriceUsd } = useEthPriceUsd();

  const onSubmit = async (formData: FormValues) => {
    if (!formData.amount || !formData.price) return;
    await trigger();
    const amount = BigInt(formData.amount);
    const price = applyDecimals(
      formData.price,
      MARKETPLACE_TOKEN.decimals,
    ).toString();
    await createOrder({ amount, price: BigInt(price) });
    reset();
    await refetchBalance();
    await refetchApprove();
    modalActions.hide();
  };

  const onMaxHandlerStake = useCallback(() => {
    if (!account || !balance || toBN(balance).isZero()) return;
    setValue(FieldNames.Amount, toBN(balance).toFixed(0), {
      shouldValidate: true,
    });
  }, [balance, account]);

  const isDisableButton = !balance || toBN(balance).isZero() || !isValid;

  const pricePerTokenUsd = useMemo(() => {
    if (!inputPrice || !ehtPriceUsd) return undefined;

    return toBN(inputPrice).times(ehtPriceUsd);
  }, [inputPrice, ehtPriceUsd]);

  const totalAmount = useMemo(() => {
    if (!inputPrice || !inputAmount) return undefined;

    const feeCoeff = toBN(1).minus(toBN(SELLER_MARKETPLACE_FEE).div(100));

    return toBN(inputAmount).times(inputPrice).times(feeCoeff);
  }, [inputAmount, inputPrice]);

  const totalAmountUsd = useMemo(() => {
    if (!totalAmount || !ehtPriceUsd) return undefined;

    return toBN(totalAmount).times(ehtPriceUsd);
  }, [ehtPriceUsd, totalAmount]);

  const fee = useMemo(() => {
    if (!totalAmount) return undefined;
    return toBN(inputAmount).times(inputPrice).minus(totalAmount);
  }, [totalAmount, inputAmount, inputPrice]);

  return (
    <ModalWrapper title="Sell">
      <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
        <div className={classes.inputContainer}>
          <div className={classes.title}>
            <Typography variant="caption1">Token Amount</Typography>
            <LoadingTypography
              variant="caption1"
              color={(theme) => theme.palette.custom.text.accent}
              isLoading={isPendingBalance}
            >
              {`Balance: ${formatBNWithCommas(balance, 0)}`} {FUSION_NFT_NAME}
              {balance && balance > 1 ? 's' : ''}
            </LoadingTypography>
          </div>
          <Controller
            control={control}
            name={FieldNames.Amount}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                value={value}
                onBlur={onBlur}
                onChange={(e) =>
                  onChange(modifyEvent(e, ONLY_POSITIVE_INTEGERS))
                }
                placeholder="0"
                onMax={onMaxHandlerStake}
                inputError={error?.message}
                disabled={!account}
              />
            )}
          />
        </div>
        <div className={classes.inputContainer}>
          <div className={classes.title}>
            <Typography variant="caption1">
              Price (per {FUSION_NFT_NAME}, in ETH)
            </Typography>
          </div>
          <Controller
            control={control}
            name={FieldNames.Price}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                value={value}
                onBlur={onBlur}
                onChange={(e) =>
                  onChange(modifyEvent(e, ONLY_DIGITS_WITH_DECIMALS))
                }
                placeholder="0"
                inputError={error?.message}
                disabled={!account}
              />
            )}
          />
        </div>

        <div className={classes.totals}>
          <div className={classes.row}>
            <Typography variant="body2">Fees</Typography>
            <Typography variant="subtitle2">
              {SELLER_MARKETPLACE_FEE}%{' '}
              {fee &&
                isValid &&
                `(${formatBNWithCommas(fee, 4, 0, true)} ${MARKETPLACE_TOKEN.symbol})`}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography variant="body2">Price per {FUSION_NFT_NAME}</Typography>
            <div className={classes.tooltipValue}>
              <Typography variant="subtitle2">
                {inputPrice && inputAmount && pricePerTokenUsd
                  ? `
                    ${formatBNWithCommas(inputPrice, 5, 0, true, true)} 
                    ${MARKETPLACE_TOKEN.symbol} 
                    ($${formatBNWithCommas(pricePerTokenUsd, 2, 0, true)})
                  `
                  : '-'}
              </Typography>
              {inputPrice && inputAmount && pricePerTokenUsd && (
                <Tooltip isInfo title="Price per token without fee" />
              )}
            </div>
          </div>
          <div className={classes.row}>
            <Typography variant="subtitle1">Total you receive</Typography>
            <div className={classes.tooltipValue}>
              <Typography variant="subtitle1">
                {totalAmount && totalAmountUsd && isValid
                  ? `${formatBNWithCommas(totalAmount, 4, 0, true)}
                      ${MARKETPLACE_TOKEN.symbol}
                      ($${formatBNWithCommas(totalAmountUsd, 2, 0, true)})
                      `
                  : '-'}
              </Typography>
              {totalAmount && (
                <Tooltip
                  isInfo
                  title="Total amount you will receive after fees"
                />
              )}
            </div>
          </div>
        </div>
        <SwitchChainButton chainId={DEFAULT_CHAIN_ID} size={Size.Large}>
          {isApproved ? (
            <Button
              disabled={isDisableButton}
              buttonType="submit"
              isLoading={isPendingCreateOrder}
            >
              Sell
            </Button>
          ) : (
            <Button onClick={approve} isLoading={isPendingApprove}>
              Approve
            </Button>
          )}
        </SwitchChainButton>
      </form>
    </ModalWrapper>
  );
};

export default SellOrderModal;
